import { useMemo, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Copy, Download, Check, AlertCircle, Mail, Plus, X } from "lucide-react";
const HEADER_LOGO_URL = "/assets/signatures/site-header-logo.png";
import { COMPANIES, renderCompanySignature, signaturePlainText } from "@/lib/companySignatures";
import { MasterSignaturePreview } from "@/components/MasterSignaturePreview";

// Pasted addresses and old prefilled links contribute only their username.
const emailUsername = (value = "") => value.trim().split("@")[0];

const INSTRUCTIONS = [
  "Generate your signature",
  "Click Copy Signature",
  "Open Outlook",
  "Go to Settings \u2192 Accounts \u2192 Signatures",
  "Create a new signature",
  "Paste and save",
];

function Field({ id, label, value, onChange, placeholder, error, type = "text" }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 inline-block"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-lg border bg-white px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-150 focus:ring-2 focus:ring-[#1479C9]/20 ${
          error
            ? "border-[#ED1C24] focus:border-[#ED1C24]"
            : "border-slate-300 focus:border-[#1479C9]"
        }`}
      />
      {error ? (
        <p
          data-testid={`${id}-error`}
          className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-[#ED1C24]"
        >
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      ) : null}
    </div>
  );
}

function AffixField({
  id,
  label,
  value,
  onChange,
  placeholder,
  error,
  prefix,
  suffix,
  inputMode,
  maxLength,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 inline-block"
      >
        {label}
      </label>
      <div
        className={`flex items-stretch overflow-hidden rounded-lg border bg-white transition-colors duration-150 focus-within:ring-2 focus-within:ring-[#1479C9]/20 ${
          error
            ? "border-[#ED1C24] focus-within:border-[#ED1C24]"
            : "border-slate-300 focus-within:border-[#1479C9]"
        }`}
      >
        {prefix ? (
          <span
            data-testid={`${id}-prefix`}
            className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-500 select-none"
          >
            {prefix}
          </span>
        ) : null}
        <input
          id={id}
          data-testid={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          className="min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 outline-none"
        />
        {suffix ? (
          <span
            data-testid={`${id}-suffix`}
            className="flex items-center border-l border-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-500 select-none whitespace-nowrap"
          >
            {suffix}
          </span>
        ) : null}
      </div>
      {error ? (
        <p
          data-testid={`${id}-error`}
          className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-[#ED1C24]"
        >
          <AlertCircle className="h-3.5 w-3.5" /> {error}
        </p>
      ) : null}
    </div>
  );
}

function App() {
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const [companyId, setCompanyId] = useState(
    COMPANIES.some((item) => item.id === params.get("company"))
      ? params.get("company") : "saksham-interiors"
  );
  const company = COMPANIES.find((item) => item.id === companyId);
  const isSaksham = companyId === "saksham-interiors";
  const [name, setName] = useState(params.get("name") || "");
  const [title, setTitle] = useState(params.get("title") || "");
  const [mobileDigits, setMobileDigits] = useState(
    (params.get("mobile") || "").replace(/\D/g, "").slice(0, 10)
  );
  const [mobile2Enabled, setMobile2Enabled] = useState(!!params.get("mobile2"));
  const [mobile2Digits, setMobile2Digits] = useState(
    (params.get("mobile2") || "").replace(/\D/g, "").slice(0, 10)
  );
  const [emailUser, setEmailUser] = useState(
    emailUsername(params.get("email") || "")
  );

  const fmtMobile = (d) =>
    d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : d ? `+91 ${d}` : "";
  const mobileFormatted = fmtMobile(mobileDigits);
  const mobile2Formatted =
    mobile2Enabled && mobile2Digits.length === 10
      ? fmtMobile(mobile2Digits)
      : "";
  const fullEmail = emailUser ? `${emailUser}${company.emailDomain}` : "";
  const data = {
    name,
    title,
    mobile: mobileFormatted,
    mobile2: mobile2Formatted,
    email: fullEmail,
  };

  const errors = useMemo(() => {
    const e = {};
    if (!mobileDigits) e.mobile = "Mobile number is required";
    else if (mobileDigits.length !== 10) e.mobile = "Enter exactly 10 digits";
    if (mobile2Enabled) {
      if (!mobile2Digits) e.mobile2 = "Mobile number is required";
      else if (mobile2Digits.length !== 10) e.mobile2 = "Enter exactly 10 digits";
    }
    if (!emailUser) e.email = "Email username is required";
    else if (
      emailUser.length > 64 ||
      !/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*$/.test(emailUser)
    ) {
      e.email = "Enter a valid email username (no spaces or leading, trailing or repeated dots)";
    }
    return e;
  }, [mobileDigits, mobile2Enabled, mobile2Digits, emailUser]);

  const canGenerate =
    name.trim().length >= 2 &&
    !errors.mobile &&
    !errors.mobile2 &&
    !errors.email &&
    Object.keys(errors).length === 0;

  const signature = useMemo(
    () => renderCompanySignature(data, companyId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, title, mobileDigits, mobile2Enabled, mobile2Digits, emailUser, companyId]
  );
  const signatureHtml = signature.html;

  // Fall back to a native rich-selection copy on browsers where ClipboardItem
  // is unsupported or fails. The return value must be checked: the legacy
  // implementation showed success even when execCommand('copy') returned false.
  const fallbackCopyRichHtml = (html) => {
    const holder = document.createElement("div");
    holder.setAttribute("contenteditable", "true");
    holder.style.cssText = "position:fixed;left:-9999px;top:0;background:#fff;";
    holder.innerHTML = html;
    document.body.appendChild(holder);
    const selection = window.getSelection();
    const previous = selection.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
    let copied = false;
    try {
      const range = document.createRange();
      range.selectNodeContents(holder);
      selection.removeAllRanges();
      selection.addRange(range);
      copied = document.execCommand("copy");
    } finally {
      selection.removeAllRanges();
      if (previous) selection.addRange(previous);
      holder.remove();
    }
    return copied;
  };

  const handleCopy = async () => {
    if (!canGenerate) {
      toast.error("Please complete the form", {
        description: "Fill in your name and a valid company email & mobile.",
      });
      return;
    }

    const html = signatureHtml;
    const plain = signaturePlainText(html);
    let copied = false;
    try {
      if (navigator.clipboard?.write && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([plain], { type: "text/plain" }),
          }),
        ]);
        copied = true;
      } else {
        copied = fallbackCopyRichHtml(html);
      }
    } catch (err) {
      try {
        copied = fallbackCopyRichHtml(html);
      } catch (fallbackError) {
        copied = false;
      }
    }

    if (!copied) {
      toast.error("Could not copy the signature", {
        description: "Allow clipboard access, then try again. You can also use Download HTML.",
      });
      return;
    }
    // Browser success confirms rich HTML reached the clipboard, not that
    // Outlook will retain remote images. The artwork is now PNG on our
    // public Cloudflare Pages domain instead of on an Emergent CDN.
    toast.success("Signature copied", {
      description: "Paste it into Outlook and save your signature.",
    });
  };

  const handleDownload = () => {
    if (!canGenerate) {
      toast.error("Please complete the form", {
        description: "Fill in your name and a valid company email & mobile.",
      });
      return;
    }
    const doc = signature.document;
    const blob = new Blob([doc], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = signature.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("HTML downloaded", {
      description: signature.filename,
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col selection:bg-sky-100 selection:text-sky-900">
      <Toaster position="top-center" richColors />

      {/* brand ribbon */}
      <div
        className="h-1 w-full"
        style={{
          background:
            "linear-gradient(90deg,#1479C9 0%,#28BCE2 25%,#702A91 55%,#ED1C24 80%,#F7941D 100%)",
        }}
      />

      {/* header */}
      <header
        data-testid="app-header"
        className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40 px-4 sm:px-6 py-4 shadow-sm"
      >
        <div className="max-w-7xl mx-auto w-full flex items-center gap-3">
          <img
            src={HEADER_LOGO_URL}
            alt="Saksham Interiors"
            className="h-9 w-9 object-contain"
          />
          <div className="leading-tight">
            <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900">
              Email Signature Generator
            </h1>
            <p className="text-xs text-slate-500 font-medium">
              Saksham Interiors Private Limited
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* form */}
          <section
            data-testid="signature-form"
            className="lg:col-span-5 bg-white rounded-xl border border-slate-200/80 p-6 sm:p-8 shadow-sm space-y-6"
          >
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-slate-800">
                Your details
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Fill these in and watch the preview update instantly.
              </p>
            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="company-selector" className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 inline-block">
                  Company
                </label>
                <select
                  id="company-selector"
                  data-testid="company-selector"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  className="w-full min-w-0 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-[#1479C9]/20"
                >
                  {COMPANIES.map((item) => (
                    <option key={item.id} value={item.id} data-testid={`company-option-${item.id}`}>{item.label}</option>
                  ))}
                </select>
              </div>
              <Field
                id="employee-name-input"
                label="Employee Name"
                value={name}
                onChange={setName}
                placeholder="e.g. Anil Kumar"
              />
              <Field
                id="job-title-input"
                label="Job Title"
                value={title}
                onChange={setTitle}
                placeholder="e.g. Project Manager"
              />
              <AffixField
                id="mobile-number-input"
                label="Mobile Number"
                prefix="+91"
                inputMode="numeric"
                maxLength={10}
                value={mobileDigits}
                onChange={(v) => setMobileDigits(v.replace(/\D/g, "").slice(0, 10))}
                placeholder="e.g. 9876543210"
                error={mobileDigits.length ? errors.mobile : undefined}
              />
              {!mobile2Enabled ? (
                <button
                  type="button"
                  data-testid="add-second-mobile-button"
                  onClick={() => setMobile2Enabled(true)}
                  className="-mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#1479C9] transition-colors duration-150 hover:text-[#0E62A8]"
                >
                  <Plus className="h-3.5 w-3.5" /> Add second mobile
                </button>
              ) : (
                <div className="-mt-1.5 space-y-1.5">
                  <AffixField
                    id="mobile-number-2-input"
                    label="Mobile Number 2"
                    prefix="+91"
                    inputMode="numeric"
                    maxLength={10}
                    value={mobile2Digits}
                    onChange={(v) =>
                      setMobile2Digits(v.replace(/\D/g, "").slice(0, 10))
                    }
                    placeholder="e.g. 9876543210"
                    error={mobile2Digits.length ? errors.mobile2 : undefined}
                  />
                  <button
                    type="button"
                    data-testid="remove-second-mobile-button"
                    onClick={() => {
                      setMobile2Enabled(false);
                      setMobile2Digits("");
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors duration-150 hover:text-[#ED1C24]"
                  >
                    <X className="h-3.5 w-3.5" /> Remove second mobile
                  </button>
                </div>
              )}
              <AffixField
                id="email-address-input"
                label="Email Address"
                suffix={company.emailDomain}
                value={emailUser}
                onChange={(v) => setEmailUser(emailUsername(v))}
                placeholder="e.g. anil.kumar"
                error={emailUser.length ? errors.email : undefined}
              />
              <p data-testid="email-domain-help" className="text-xs text-slate-500">
                Enter only your email username. The selected company's domain is added automatically.
              </p>
            </div>

            <div className="rounded-lg bg-[#F1F5F9] border border-slate-200/70 px-4 py-3 flex items-start gap-2.5">
              <Mail className="h-4 w-4 text-[#1479C9] mt-0.5 shrink-0" />
              <p className="text-xs text-slate-600 leading-relaxed">
                Company details, office number, website and address are fixed to
                {isSaksham ? " the Saksham standard" : " the selected company's approved template"} and added automatically.
              </p>
            </div>
          </section>

          {/* preview + actions */}
          <section className="min-w-0 lg:col-span-7 space-y-6">
            <div
              data-testid="preview-card"
              className="bg-white rounded-xl border border-slate-200/80 shadow-sm -mx-4 sm:mx-0 sm:p-8"
            >
              <div className="flex items-center justify-between mb-5 px-4 pt-4 sm:p-0">
                <h2 className="text-lg font-semibold tracking-tight text-slate-800">
                  Live preview
                </h2>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Outlook ready
                </span>
              </div>

              <div className="bg-white">
                {canGenerate ? (
                  <MasterSignaturePreview document={signature.document} companyName={company.label} />
                ) : (
                  <div
                    data-testid="signature-preview-empty"
                    className="min-h-[160px] flex items-center justify-center text-center"
                  >
                    <p className="text-sm text-slate-400 max-w-xs">
                      Enter your name, a valid company email and mobile number to
                      preview your signature.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 px-4 pb-4 sm:p-0">
                <button
                  data-testid="copy-signature-button"
                  onClick={handleCopy}
                  disabled={!canGenerate}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#1479C9] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-[#0E62A8] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Copy className="h-4 w-4" /> Copy Signature
                </button>
                <button
                  data-testid="download-html-button"
                  onClick={handleDownload}
                  disabled={!canGenerate}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 shadow-sm transition-all duration-150 hover:bg-slate-50 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Download className="h-4 w-4" /> Download HTML
                </button>
              </div>
            </div>

            {/* instructions */}
            <div
              data-testid="outlook-instructions-panel"
              className="bg-white rounded-xl border border-slate-200/80 p-6 sm:p-8 shadow-sm"
            >
              <h2 className="text-base font-semibold text-slate-900 mb-4">
                Add it to Outlook
              </h2>
              <ol className="space-y-3">
                {INSTRUCTIONS.map((step, i) => (
                  <li
                    key={i}
                    data-testid={`instruction-step-${i + 1}`}
                    className="flex items-start gap-3"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1479C9]/10 text-xs font-bold text-[#1479C9]">
                      {i + 1}
                    </span>
                    <span className="text-sm text-slate-600 leading-relaxed pt-0.5">
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white py-5">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 flex items-center gap-2">
          <Check className="h-3.5 w-3.5 text-[#F7941D]" />
          <p className="text-xs text-slate-400">
            Runs entirely in your browser. No login, no data stored.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
